import Loader from "components/DesignSystem/Loader/loader";
import { useWindowSize } from "hooks/useWindowSize";
import Async from "components/DesignSystem/AsyncComponents/Async";
import {
  Filter,
  MultiSelectFilter,
} from "components/DesignSystem/Filter/Filter";
import { PriceInput } from "components/PriceInput/PriceInput";
import { Chip } from "components/DesignSystem/Chips/Chips";
import { formatDateByViewType } from "utils/formatDate";
import { YYYY_MM_DD } from "constants/date";
import { Form, Formik } from "formik";
import { useUpdateQuery } from "hooks/useQuery";
import React, { ChangeEvent, ComponentProps, useMemo, useState } from "react";
import { debounce } from "utils/debouncing";
import { UpdateFilterFunction } from "hooks/useFilter";
import * as DATE_PERIOD from "constants/dateFilter";
import {
  INVOICE_STATUSES,
  INVOICE_STATUSES_OPTIONS,
  INVOICE_STATUSES_TYPE,
  INVOICE_TYPE,
  INVOICE_TYPE_LABEL,
  INVOICE_TYPE_TYPE,
  STATUS_GROUP_DATA_MAP,
  STATUS_GROUP_TYPES,
  STATUS_GROUP_TYPES_TYPE,
} from "constants/invoicing";
import {
  REVENUE_DATA_SOURCE,
  REVENUE_DATA_SOURCE_OPTIONS,
  REVENUE_DATA_SOURCE_TYPES,
} from "constants/revenueDataSource";
import {
  DateRangeValue,
  getDateRange,
  getDateRangeWithSetting,
} from "utils/getDateRange";
import { useGetStartingDateQuery } from "store/apis/chartOfAccounts";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { usePaginatedQuery } from "hooks/usePaginatedQuery";
import {
  useLazyGetAllInvoiceCustomersListQuery,
  useGetInvoiceCustomerStatesQuery,
} from "store/apis/invoices";
import { DateFilter } from "components/Filters/DateFilter";
import { BooksDateFilter } from "components/Filters/BooksDateFilter";
import { PERIOD } from "constants/revenueMetrics";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import { Button } from "components/DesignSystem/Button/Button";
import { GroupInvoiceCustomer } from "types/Models/invoice";
import { getDatePeriod } from "utils/getDatePeriod";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";

export type InvoiceFilterType = {
  INVOICE_START_DATE: string;
  INVOICE_END_DATE: string;
  DUEDATE_START_DATE: string;
  DUEDATE_END_DATE: string;
  VOID_START_DATE: string;
  VOID_END_DATE: string;
  UNCOLLECTIBLE_START_DATE: string;
  UNCOLLECTIBLE_END_DATE: string;
  INVOICE_DATE: string;
  DUE_DATE: string;
  SOURCE: REVENUE_DATA_SOURCE_TYPES[];
  STATUS: INVOICE_STATUSES_TYPE[];
  STATUS_GROUP: STATUS_GROUP_TYPES_TYPE | null;
  CUSTOMER: string[];
  CUSTOMER_STATE: string[];
  MIN_AMOUNT: string;
  MAX_AMOUNT: string;
  INVOICE_TYPE: INVOICE_TYPE_TYPE;
};

export const DUE_DATE_PERIOD = [
  { label: "Current week", value: DATE_PERIOD.CURRENT_WEEK },
  { label: "Next week", value: DATE_PERIOD.NEXT_WEEK },
  { label: "Current month", value: DATE_PERIOD.CURRENT_MONTH },
  { label: "Last month", value: DATE_PERIOD.LAST_MONTH },
  { label: "Next month", value: DATE_PERIOD.NEXT_MONTH },
  { label: "Current quarter", value: DATE_PERIOD.CURRENT_QUARTER },
  { label: "Last quarter", value: DATE_PERIOD.LAST_QUARTER },
  { label: "Next quarter", value: DATE_PERIOD.NEXT_QUARTER },
  { label: "Current year", value: DATE_PERIOD.CURRENT_YEAR },
  { label: "Last year", value: DATE_PERIOD.LAST_YEAR },
];

const DATE_FILTER_TYPE = {
  INVOICE: "INVOICE",
  DUE_DATE: "DUE_DATE",
} as const;

type DATE_FILTER_TYPE_TYPE = keyof typeof DATE_FILTER_TYPE;

const Amount = ({
  filter,
  updateFilter,
}: {
  filter: InvoiceFilterType;
  updateFilter: UpdateFilterFunction<InvoiceFilterType>;
}) => {
  const minAmount = filter.MIN_AMOUNT;
  const maxAmount = filter.MAX_AMOUNT;

  const handleAmountChange = debounce((e: ChangeEvent<HTMLFormElement>) => {
    let { value, name } = e.target;
    const numericValue = value.replace(/[^\d.-]/g, "");

    if (name === "MIN_AMOUNT") {
      updateFilter(name, numericValue);
    } else if (name === "MAX_AMOUNT") {
      updateFilter(name, numericValue);
    }
  });

  return (
    <Formik
      initialValues={{
        MIN_AMOUNT: minAmount || "",
        MAX_AMOUNT: maxAmount || "",
      }}
      onSubmit={() => {}}
    >
      {({ setValues }) => {
        return (
          <div className="t-flex t-flex-col t-gap-5">
            <div className="t-flex t-flex-col t-gap-3">
              <Form
                className="all:unset t-flex t-flex-col t-gap-4"
                onChange={handleAmountChange}
              >
                <PriceInput
                  name="MIN_AMOUNT"
                  placeholder="0.00"
                  label="Min. Amount"
                />
                <PriceInput
                  name="MAX_AMOUNT"
                  placeholder="0.00"
                  label="Max. Amount"
                />
              </Form>
            </div>
          </div>
        );
      }}
    </Formik>
  );
};

const statusGroupFilter = ({
  filter,
  updateFilter,
  selectedStatusGroup,
  selectedStatus,
}: {
  filter: InvoiceFilterType;
  updateFilter: UpdateFilterFunction<InvoiceFilterType>;
  selectedStatusGroup?: STATUS_GROUP_TYPES_TYPE;
  selectedStatus?: INVOICE_STATUSES_TYPE[];
}) => {
  if (selectedStatusGroup) {
    updateFilter("STATUS_GROUP", selectedStatusGroup);
    updateFilter("STATUS", STATUS_GROUP_DATA_MAP[selectedStatusGroup].statuses);
    return;
  }

  if (selectedStatus) {
    updateFilter("STATUS", selectedStatus);
    if (selectedStatus.length > 0) {
      updateFilter("STATUS_GROUP", STATUS_GROUP_TYPES.ACTIVE);
    }
  }
};

const StatusFilter = ({
  filter,
  updateFilter,
}: {
  filter: InvoiceFilterType;
  updateFilter: UpdateFilterFunction<InvoiceFilterType>;
}) => {
  const selectedStatus = filter.STATUS || [];

  return (
    <span className="t-flex t-flex-col ">
      <div className="t-self-end">
        <Button
          onClick={() => {
            updateFilter("STATUS_GROUP", null);
            updateFilter("STATUS", []);
          }}
          customType="text"
          size="xsmall"
        >
          <span>Reset</span>
        </Button>
      </div>
      <RadioGroup.Root
        value={filter.STATUS_GROUP ?? ""}
        onValueChange={(v: STATUS_GROUP_TYPES_TYPE) => {
          statusGroupFilter({
            filter,
            updateFilter,
            selectedStatusGroup: v,
          });
        }}
      >
        <RadioGroup.Content className="t-flex t-flex-col">
          <RadioGroup.Item
            align="start"
            value={STATUS_GROUP_TYPES.ACTIVE}
            className="!t-h-auto"
            checked={
              filter.STATUS_GROUP === STATUS_GROUP_TYPES.ACTIVE &&
              selectedStatus.length > 0
            }
          >
            <p>Active</p>
            <span className=" t-flex t-flex-col t-gap-2">
              {INVOICE_STATUSES_OPTIONS.map(({ value, label }) => {
                return (
                  <Checkbox
                    key={value}
                    label={<>{label}</>}
                    name={value}
                    checked={selectedStatus.includes(value)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        statusGroupFilter({
                          filter,
                          updateFilter,
                          selectedStatus: [...selectedStatus, value],
                        });
                      } else {
                        statusGroupFilter({
                          filter,
                          updateFilter,
                          selectedStatus: selectedStatus.filter(
                            (s) => s !== value
                          ),
                        });
                      }
                    }}
                  />
                );
              })}
            </span>
          </RadioGroup.Item>
          <RadioGroup.Item
            align="start"
            value={STATUS_GROUP_TYPES.DRAFT}
            className="!t-h-auto t-mt-4"
            checked={filter.STATUS_GROUP === STATUS_GROUP_TYPES.DRAFT}
          >
            <p>Draft</p>
          </RadioGroup.Item>
          <RadioGroup.Item
            align="start"
            value={STATUS_GROUP_TYPES.VOID}
            className="!t-h-auto"
            checked={filter.STATUS_GROUP === STATUS_GROUP_TYPES.VOID}
          >
            <p>Void</p>
          </RadioGroup.Item>
        </RadioGroup.Content>
      </RadioGroup.Root>
    </span>
  );
};
export const SourceFilter = ({
  filter,
  updateFilter,
}: {
  filter: { SOURCE: REVENUE_DATA_SOURCE_TYPES[] };
  updateFilter: (name: "SOURCE", value: REVENUE_DATA_SOURCE_TYPES[]) => void;
}) => {
  const sourceList = [
    REVENUE_DATA_SOURCE.HUBSPOT,
    REVENUE_DATA_SOURCE.JUSTPAID,
    REVENUE_DATA_SOURCE.MANUAL,
    REVENUE_DATA_SOURCE.STRIPE,
    REVENUE_DATA_SOURCE.BILL_COM,
    REVENUE_DATA_SOURCE.QBO_IMPORT,
    REVENUE_DATA_SOURCE.MERCURY,
  ];

  const selectedSource = filter.SOURCE || [];

  return (
    <MultiSelectFilter
      options={sourceList.map((label) => ({
        value: label,
        label:
          REVENUE_DATA_SOURCE_OPTIONS[
            label as keyof typeof REVENUE_DATA_SOURCE_OPTIONS
          ],
      }))}
      selected={selectedSource}
      onChange={(newValues) =>
        updateFilter("SOURCE", newValues as REVENUE_DATA_SOURCE_TYPES[])
      }
      isSearchable={false}
      isSelectAll={false}
    />
  );
};

const InvoiceDateFilter = ({
  filterType,
  filter,
  updateFilter,
}: {
  filterType: DATE_FILTER_TYPE_TYPE;
  filter: InvoiceFilterType;
  updateFilter: UpdateFilterFunction<InvoiceFilterType>;
}) => {
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const { data: bookkeepingSettings } = useGetBookkeepingSettingsQuery(
    { entityId },
    { skip: !entityId }
  );
  const { data: startDateData } = useGetStartingDateQuery(
    { groupId, entityId },
    { skip: !groupId || !entityId }
  );
  const bookkeepingStartDate = startDateData?.start_date;

  const onDateChange: ComponentProps<typeof BooksDateFilter>["updateFilter"] = (
    name,
    value
  ) => {
    if (!value) return;

    if (name === "SELECT_PERIOD" && value !== "CUSTOM") {
      const { startDate, endDate } = getDateRangeWithSetting(
        value as DateRangeValue,
        bookkeepingSettings?.tax_year_end,
        bookkeepingStartDate
      );
      const startDateObj = startDate.format(YYYY_MM_DD);
      const endDateObj = endDate.format(YYYY_MM_DD);
      updateFilter("INVOICE_START_DATE", startDateObj);
      updateFilter("INVOICE_END_DATE", endDateObj);
      return;
    }

    if (name === "START_DATE") updateFilter("INVOICE_START_DATE", value);
    if (name === "END_DATE") updateFilter("INVOICE_END_DATE", value);
  };

  const values = {
    SELECT_PERIOD: "",
    START_DATE: filter.INVOICE_START_DATE || "",
    END_DATE: filter.INVOICE_END_DATE || "",
  };

  return (
    <div className="t-flex t-flex-col t-gap-3 t-w-full">
      <BooksDateFilter
        datePeriod={
          filterType === DATE_FILTER_TYPE.DUE_DATE
            ? DUE_DATE_PERIOD
            : getDatePeriod
        }
        skipDateUpdateOnPeriodChange
        pickerType="date"
        values={values}
        updateFilter={onDateChange}
      />
    </div>
  );
};

export const DueDateFilter = ({
  filterType,
  filter,
  updateFilter,
}: {
  filterType: DATE_FILTER_TYPE_TYPE;
  filter: InvoiceFilterType;
  updateFilter: UpdateFilterFunction<InvoiceFilterType>;
}) => {
  const onDateChange: ComponentProps<typeof DateFilter>["updateFilter"] = (
    name,
    value
  ) => {
    if (!value) return;

    if (name === "SELECT_PERIOD" && value !== "CUSTOM") {
      const { startDate, endDate } = getDateRange(value as DateRangeValue);
      const startDateObj = startDate.format(YYYY_MM_DD);
      const endDateObj = endDate.format(YYYY_MM_DD);
      updateFilter("DUEDATE_START_DATE", startDateObj);
      updateFilter("DUEDATE_END_DATE", endDateObj);

      return;
    }

    if (name === "START_DATE") updateFilter("DUEDATE_START_DATE", value);
    if (name === "END_DATE") updateFilter("DUEDATE_END_DATE", value);
  };

  const values = {
    SELECT_PERIOD: "",
    START_DATE: filter.DUEDATE_START_DATE || "",
    END_DATE: filter.DUEDATE_END_DATE || "",
  };

  return (
    <div className="t-flex t-flex-col t-gap-3 t-w-full">
      <DateFilter
        datePeriod={
          filterType === DATE_FILTER_TYPE.DUE_DATE ? DUE_DATE_PERIOD : undefined
        }
        skipDateUpdateOnPeriodChange
        pickerType="date"
        values={values}
        updateFilter={onDateChange}
        maxEndDate={undefined}
      />
    </div>
  );
};

export const VoidDateFilter = ({
  filter,
  updateFilter,
}: {
  filter: InvoiceFilterType;
  updateFilter: UpdateFilterFunction<InvoiceFilterType>;
}) => {
  const onDateChange: ComponentProps<typeof DateFilter>["updateFilter"] = (
    name,
    value
  ) => {
    if (!value) return;

    if (name === "SELECT_PERIOD" && value !== "CUSTOM") {
      const { startDate, endDate } = getDateRange(value as DateRangeValue);
      updateFilter("VOID_START_DATE", startDate.format(YYYY_MM_DD));
      updateFilter("VOID_END_DATE", endDate.format(YYYY_MM_DD));

      return;
    }

    if (name === "START_DATE") updateFilter("VOID_START_DATE", value);
    if (name === "END_DATE") updateFilter("VOID_END_DATE", value);
  };

  const values = {
    SELECT_PERIOD: "",
    START_DATE: filter.VOID_START_DATE || "",
    END_DATE: filter.VOID_END_DATE || "",
  };

  return (
    <div className="t-flex t-flex-col t-gap-3 t-w-full">
      <DateFilter
        skipDateUpdateOnPeriodChange
        pickerType="date"
        values={values}
        updateFilter={onDateChange}
        maxEndDate={undefined}
      />
    </div>
  );
};

export const UncollectibleDateFilter = ({
  filter,
  updateFilter,
}: {
  filter: InvoiceFilterType;
  updateFilter: UpdateFilterFunction<InvoiceFilterType>;
}) => {
  const onDateChange: ComponentProps<typeof DateFilter>["updateFilter"] = (
    name,
    value
  ) => {
    if (!value) return;

    if (name === "SELECT_PERIOD" && value !== "CUSTOM") {
      const { startDate, endDate } = getDateRange(value as DateRangeValue);
      updateFilter("UNCOLLECTIBLE_START_DATE", startDate.format(YYYY_MM_DD));
      updateFilter("UNCOLLECTIBLE_END_DATE", endDate.format(YYYY_MM_DD));

      return;
    }

    if (name === "START_DATE") updateFilter("UNCOLLECTIBLE_START_DATE", value);
    if (name === "END_DATE") updateFilter("UNCOLLECTIBLE_END_DATE", value);
  };

  const values = {
    SELECT_PERIOD: "",
    START_DATE: filter.UNCOLLECTIBLE_START_DATE || "",
    END_DATE: filter.UNCOLLECTIBLE_END_DATE || "",
  };

  return (
    <div className="t-flex t-flex-col t-gap-3 t-w-full">
      <DateFilter
        skipDateUpdateOnPeriodChange
        pickerType="date"
        values={values}
        updateFilter={onDateChange}
        maxEndDate={undefined}
      />
    </div>
  );
};

const CustomerFilter = ({
  updateFilter,
  filter,
}: {
  updateFilter: UpdateFilterFunction<InvoiceFilterType>;
  filter: InvoiceFilterType;
}) => {
  const group = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const [customerSearchValue, setCustomerSearchValue] = useState("");

  const {
    data: invoiceCustomers,
    loadNext,
    isLoading,
    isSuccess,
  } = usePaginatedQuery<{
    client_customers?: GroupInvoiceCustomer[];
  }>(useLazyGetAllInvoiceCustomersListQuery, "client_customers", {
    groupId: group.uuid,
    entityId: entityId,
    searchTerm: customerSearchValue,
  });

  const updateFilterCallback = (newValues: string[]) => {
    updateFilter("CUSTOMER", newValues as string[]);
  };

  const onCustomerSearch = debounce((value) => setCustomerSearchValue(value));

  if (isLoading && !isSuccess) {
    return (
      <div className="t-w-full t-h-full t-flex t-justify-center t-align-middle">
        <Loader />
      </div>
    );
  }

  if (invoiceCustomers?.client_customers) {
    let selectedCustomers: string[] = [];
    if (filter.CUSTOMER.length > 0) {
      selectedCustomers = filter.CUSTOMER;
    } else if (Array.isArray(filter.CUSTOMER)) {
      selectedCustomers = filter.CUSTOMER;
    }
    return (
      <MultiSelectFilter
        onSearchValueChange={onCustomerSearch}
        hasMore={true}
        totalCount={invoiceCustomers?.client_customers.length}
        loadNext={loadNext}
        onChange={updateFilterCallback}
        options={invoiceCustomers?.client_customers.map(({ name, uuid }) => ({
          value: uuid,
          label: name,
        }))}
        selected={selectedCustomers}
      />
    );
  }

  return null;
};

const StateFilter = ({
  filter,
  updateFilter,
}: {
  filter: InvoiceFilterType;
  updateFilter: UpdateFilterFunction<InvoiceFilterType>;
}) => {
  const group = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { data, isLoading, isSuccess, isError } =
    useGetInvoiceCustomerStatesQuery({
      groupId: group.uuid,
      entityId,
    });

  const updateFilterCallback = (newValues: string[]) => {
    updateFilter("CUSTOMER_STATE", newValues);
  };

  const stateOptions = (data?.states || []).map((state) => ({
    value: state,
    label: state,
  }));

  return (
    <Async.Root
      isLoading={isLoading}
      isSuccess={isSuccess}
      isError={isError}
      isEmpty={stateOptions.length === 0}
    >
      <Async.Empty>
        <MultiSelectFilter
          options={stateOptions}
          selected={filter.CUSTOMER_STATE || []}
          onChange={updateFilterCallback}
          isSelectAll={false}
        />
      </Async.Empty>
      <Async.Success>
        <MultiSelectFilter
          options={stateOptions}
          selected={filter.CUSTOMER_STATE || []}
          onChange={updateFilterCallback}
        />
      </Async.Success>
    </Async.Root>
  );
};

const InvoiceTypeFilter = ({
  filter,
  updateFilter,
}: {
  filter: InvoiceFilterType;
  updateFilter: UpdateFilterFunction<InvoiceFilterType>;
}) => {
  const isRecurringSelected = filter.INVOICE_TYPE === INVOICE_TYPE.RECURRING;

  return (
    <div className="t-flex t-flex-col t-gap-3">
      <Checkbox
        label={<>{INVOICE_TYPE_LABEL.RECURRING}</>}
        name="recurring"
        checked={isRecurringSelected}
        onChange={(e) => {
          if (e.target.checked) {
            updateFilter("INVOICE_TYPE", INVOICE_TYPE.RECURRING);
          } else {
            updateFilter("INVOICE_TYPE", "" as INVOICE_TYPE_TYPE);
          }
        }}
      />
    </div>
  );
};

const formatDateRangeLabel = (startDate: string, endDate: string) => {
  if (startDate && endDate) {
    return `${formatDateByViewType(
      startDate,
      PERIOD.TOTALS
    )} - ${formatDateByViewType(endDate, PERIOD.TOTALS)}`;
  }
  if (startDate) {
    return `Since ${formatDateByViewType(startDate, PERIOD.TOTALS)}`;
  }
  return `Till ${formatDateByViewType(endDate, PERIOD.TOTALS)}`;
};

export const InvoiceFilter = ({
  filter,
  updateFilter,
}: {
  filter: InvoiceFilterType;
  updateFilter: UpdateFilterFunction<InvoiceFilterType>;
}) => {
  const appliedFilterCount = useMemo(
    () =>
      Object.keys(filter).reduce((acc, key) => {
        if (key === "STATUS_GROUP") return acc;
        if (
          key === "STATUS" ||
          key === "SOURCE" ||
          key === "CUSTOMER" ||
          key === "CUSTOMER_STATE"
        ) {
          return acc + (Array.isArray(filter[key]) ? filter[key].length : 0);
        }
        if (filter[key as keyof InvoiceFilterType]) {
          return acc + 1;
        }
        return acc;
      }, 0),
    [filter]
  );

  const { isMobile } = useWindowSize();

  return (
    <div className="t-flex t-gap-2 t-items-center">
      <Filter.Root
        defaultValue="source"
        title={
          appliedFilterCount > 0 ? (
            <span className="t-text-text-30">({appliedFilterCount})</span>
          ) : null
        }
        hideDivider={isMobile}
        capsule={
          <span className="t-contents sm:t-hidden">
            <Chip
              onClick={() => {
                statusGroupFilter({
                  filter,
                  updateFilter,
                  selectedStatus: [INVOICE_STATUSES.OVERDUE],
                });
              }}
              onRemove={() => {
                updateFilter(
                  "STATUS",
                  filter.STATUS?.filter(
                    (sts) => sts !== INVOICE_STATUSES.OVERDUE
                  ) || []
                );
                updateFilter("STATUS_GROUP", null);
              }}
              isRemovable={true}
              isFixedFilter={true}
              isActive={filter.STATUS?.includes(INVOICE_STATUSES.OVERDUE)}
              filterType="status"
            >
              Overdue
            </Chip>
            <Chip
              onClick={() => {
                statusGroupFilter({
                  filter,
                  updateFilter,
                  selectedStatusGroup: STATUS_GROUP_TYPES.DRAFT,
                });
              }}
              onRemove={() => {
                updateFilter("STATUS_GROUP", null);
              }}
              isRemovable={true}
              isFixedFilter={true}
              isActive={filter.STATUS_GROUP === INVOICE_STATUSES.DRAFT}
              filterType="status"
            >
              Draft
            </Chip>
            {filter.STATUS_GROUP &&
              filter.STATUS_GROUP === STATUS_GROUP_TYPES.VOID && (
                <Chip
                  onRemove={() => {
                    updateFilter("STATUS_GROUP", null);
                  }}
                  isRemovable={true}
                  isActive
                  filterType="status"
                >
                  {STATUS_GROUP_DATA_MAP[STATUS_GROUP_TYPES.VOID].label}
                </Chip>
              )}
            {(filter.INVOICE_START_DATE || filter.INVOICE_END_DATE) && (
              <Chip
                filterType="invoiceDate"
                onRemove={() => {
                  updateFilter("INVOICE_START_DATE", "");
                  updateFilter("INVOICE_END_DATE", "");
                }}
                isRemovable={true}
                isActive
              >
                {"Invoice Date: "}
                {formatDateRangeLabel(
                  filter.INVOICE_START_DATE,
                  filter.INVOICE_END_DATE
                )}
              </Chip>
            )}
            {(filter.DUEDATE_START_DATE || filter.DUEDATE_END_DATE) && (
              <Chip
                onRemove={() => {
                  updateFilter("DUEDATE_START_DATE", "");
                  updateFilter("DUEDATE_END_DATE", "");
                }}
                isRemovable={true}
                isActive
                filterType="dueDate"
              >
                {"Due Date: "}
                {formatDateRangeLabel(
                  filter.DUEDATE_START_DATE,
                  filter.DUEDATE_END_DATE
                )}
              </Chip>
            )}
            {(filter.VOID_START_DATE || filter.VOID_END_DATE) && (
              <Chip
                onRemove={() => {
                  updateFilter("VOID_START_DATE", "");
                  updateFilter("VOID_END_DATE", "");
                }}
                isRemovable={true}
                isActive
                filterType="voidDate"
              >
                {"Void Date: "}
                {formatDateRangeLabel(
                  filter.VOID_START_DATE,
                  filter.VOID_END_DATE
                )}
              </Chip>
            )}
            {(filter.UNCOLLECTIBLE_START_DATE ||
              filter.UNCOLLECTIBLE_END_DATE) && (
              <Chip
                onRemove={() => {
                  updateFilter("UNCOLLECTIBLE_START_DATE", "");
                  updateFilter("UNCOLLECTIBLE_END_DATE", "");
                }}
                isRemovable={true}
                isActive
                filterType="uncollectibleDate"
              >
                {"Uncollectible Date: "}
                {formatDateRangeLabel(
                  filter.UNCOLLECTIBLE_START_DATE,
                  filter.UNCOLLECTIBLE_END_DATE
                )}
              </Chip>
            )}
            {filter.STATUS && filter.STATUS.length > 0 && (
              <Chip
                key={"status"}
                onRemove={() => {
                  updateFilter("STATUS_GROUP", null);
                  statusGroupFilter({
                    filter,
                    updateFilter,
                    selectedStatus: [],
                  });
                }}
                isRemovable={true}
                isActive
                filterType="status"
              >
                {`Active (${filter.STATUS.length})`}
              </Chip>
            )}
            {filter.SOURCE && filter.SOURCE && filter.SOURCE.length > 0 && (
              <Chip
                onRemove={() => {
                  updateFilter("SOURCE", []);
                }}
                isRemovable={true}
                isActive
                filterType="source"
              >
                {`Sources (${filter.SOURCE.length})`}
              </Chip>
            )}
            {filter.MIN_AMOUNT && filter.MIN_AMOUNT.length > 0 && (
              <Chip
                onRemove={() => {
                  updateFilter("MIN_AMOUNT", "");
                }}
                isRemovable={true}
                isActive
                filterType="amount"
              >
                Min Amount ${filter.MIN_AMOUNT}
              </Chip>
            )}
            {filter.MAX_AMOUNT && filter.MAX_AMOUNT.length > 0 && (
              <Chip
                onRemove={() => {
                  updateFilter("MAX_AMOUNT", "");
                }}
                isRemovable={true}
                isActive
                filterType="amount"
              >
                Max Amount ${filter.MAX_AMOUNT}
              </Chip>
            )}
            {filter.CUSTOMER.length > 0 && (
              <Chip
                onRemove={() => {
                  updateFilter("CUSTOMER", []);
                }}
                isRemovable={true}
                isActive
                filterType="customer"
              >
                Customer ({filter.CUSTOMER.length})
              </Chip>
            )}
            {filter.CUSTOMER_STATE.length > 0 && (
              <Chip
                onRemove={() => {
                  updateFilter("CUSTOMER_STATE", []);
                }}
                isRemovable={true}
                isActive
                filterType="customerState"
              >
                Customer State ({filter.CUSTOMER_STATE.length})
              </Chip>
            )}
            {filter.INVOICE_TYPE &&
              filter.INVOICE_TYPE === INVOICE_TYPE.RECURRING && (
                <Chip
                  onRemove={() => {
                    updateFilter("INVOICE_TYPE", "" as INVOICE_TYPE_TYPE);
                  }}
                  isRemovable={true}
                  isActive
                  filterType="invoiceType"
                >
                  {INVOICE_TYPE_LABEL.RECURRING}
                </Chip>
              )}
          </span>
        }
      >
        <Filter.Portal>
          <Filter.List>
            <Filter.ListItem value="source">Source</Filter.ListItem>
            <Filter.ListItem value="customer">Customer</Filter.ListItem>
            <Filter.ListItem value="customerState">
              Customer State
            </Filter.ListItem>
            <Filter.ListItem value="amount">Amount</Filter.ListItem>
            <Filter.ListItem value="invoiceDate">Invoice Date</Filter.ListItem>
            <Filter.ListItem value="dueDate">Due Date</Filter.ListItem>
            <Filter.ListItem value="voidDate">Void Date</Filter.ListItem>
            <Filter.ListItem value="uncollectibleDate">
              Uncollectible Date
            </Filter.ListItem>
            <Filter.ListItem value="status">Status</Filter.ListItem>
            <Filter.ListItem value="invoiceType">Others</Filter.ListItem>
          </Filter.List>
          <Filter.Body value="amount" block>
            <Amount filter={filter} updateFilter={updateFilter} />
          </Filter.Body>
          <Filter.Body value="invoiceDate" block>
            <InvoiceDateFilter
              filterType={DATE_FILTER_TYPE.INVOICE}
              filter={filter}
              updateFilter={updateFilter}
            />
          </Filter.Body>
          <Filter.Body value="dueDate" block>
            <DueDateFilter
              filterType={DATE_FILTER_TYPE.DUE_DATE}
              filter={filter}
              updateFilter={updateFilter}
            />
          </Filter.Body>
          <Filter.Body value="voidDate" block>
            <VoidDateFilter filter={filter} updateFilter={updateFilter} />
          </Filter.Body>
          <Filter.Body value="uncollectibleDate" block>
            <UncollectibleDateFilter
              filter={filter}
              updateFilter={updateFilter}
            />
          </Filter.Body>
          <Filter.Body value="status" block>
            <StatusFilter filter={filter} updateFilter={updateFilter} />
          </Filter.Body>
          <Filter.Body value="source">
            <SourceFilter filter={filter} updateFilter={updateFilter} />
          </Filter.Body>
          <Filter.Body value="customer" block>
            <CustomerFilter updateFilter={updateFilter} filter={filter} />
          </Filter.Body>
          <Filter.Body value="customerState" block>
            <StateFilter filter={filter} updateFilter={updateFilter} />
          </Filter.Body>
          <Filter.Body value="invoiceType" block>
            <InvoiceTypeFilter filter={filter} updateFilter={updateFilter} />
          </Filter.Body>
        </Filter.Portal>
      </Filter.Root>
    </div>
  );
};
